import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ICE, VIDEO_360, iceQueue, startPolling } from "./rtc";
import { fmtIT } from "@/lib/dateIT";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/trattative/public/video`;
const msg = (e, d) => e.response?.data?.detail || d;

function ConfermaDati({ token, info }) {
  const [sent, setSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(!!info.identita_confermata_at);
  const d = info.dati_identita;
  if (ok) return <div className="rounded-lg bg-emerald-50 text-emerald-800 text-sm p-3" data-testid="vc-id-ok">Dati confermati. Grazie.</div>;
  const richiedi = async () => { setErr(""); try { await axios.post(`${API}/${token}/identita/request-otp`); setSent(true); } catch (e) { setErr(msg(e, "Errore")); } };
  const conferma = async () => { setErr(""); try { await axios.post(`${API}/${token}/identita/conferma`, { otp }); setOk(true); } catch (e) { setErr(msg(e, "Codice non valido")); } };
  return (
    <div className="rounded-xl border border-teal-200 bg-white p-4 space-y-2 text-slate-800" data-testid="vc-id">
      <p className="text-sm font-semibold">Verifichi i suoi dati di identificazione</p>
      <div className="text-sm grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
        <span className="text-slate-500">Nome</span><b>{d.nome}</b>
        {d.codice_fiscale && <><span className="text-slate-500">Cod. fiscale</span><span>{d.codice_fiscale}</span></>}
        <span className="text-slate-500">Documento</span><span>{d.tipo_documento} n. {d.numero_documento}</span>
        <span className="text-slate-500">Scadenza</span><span>{fmtIT(d.scadenza)}</span>
      </div>
      {err && <p className="text-xs text-red-600" data-testid="vc-id-err">{err}</p>}
      {!sent ? <button onClick={richiedi} className="w-full bg-teal-700 text-white rounded-lg py-2 text-sm font-medium" data-testid="vc-id-otp">I dati sono corretti: ricevi il codice via SMS ({info.destinazione})</button> : (
        <div className="flex gap-2">
          <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} maxLength={6} inputMode="numeric" placeholder="Codice OTP" className="flex-1 border rounded-lg px-3 text-center tracking-widest" data-testid="vc-id-otp-input" />
          <button onClick={conferma} disabled={otp.length < 6} className="bg-emerald-600 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50" data-testid="vc-id-conferma">Conferma</button>
        </div>
      )}
      <p className="text-[11px] text-slate-500">Se un dato è errato, lo dica all'operatore prima di confermare.</p>
    </div>
  );
}

function useClientCall(token, active, localRef, remoteRef, setExtra) {
  const pcRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [facing, setFacing] = useState("user");
  useEffect(() => {
    if (!active) return undefined;
    const cid = Math.random().toString(36).slice(2);
    let q = null, st = null, stop = () => {};
    const send = (tipo, dati) => axios.post(`${API}/${token}/segnali`, { tipo, dati }).catch(() => {});
    let annullato = false;
    navigator.mediaDevices.getUserMedia({ video: { ...VIDEO_360, facingMode: "user" }, audio: true }).then((m) => {
      if (annullato) { m.getTracks().forEach((x) => x.stop()); return; }
      st = m; setStream(m); if (localRef.current) localRef.current.srcObject = m;
      send("pronto", { cid });
      stop = startPolling((n) => axios.get(`${API}/${token}/segnali?dopo=${n}`), async (batch, data) => {
        setExtra((x) => ({ ...x, ...data }));
        for (const s of batch) {
          const d = s.dati || {};
          if (d.cid !== cid) continue;
          if (s.tipo === "offer") {
            pcRef.current?.close();
            const pc = new RTCPeerConnection(ICE); pcRef.current = pc; q = iceQueue(pc);
            m.getTracks().forEach((tr) => pc.addTrack(tr, m));
            pc.ontrack = (e) => { if (remoteRef.current) remoteRef.current.srcObject = e.streams[0]; };
            pc.onicecandidate = (e) => { if (e.candidate) send("ice", { cid, c: e.candidate.toJSON() }); };
            await pc.setRemoteDescription({ type: "offer", sdp: d.sdp }); await q.flush();
            await pc.setLocalDescription(await pc.createAnswer());
            send("answer", { cid, sdp: pc.localDescription.sdp });
          } else if (s.tipo === "ice" && q) await q.add(d.c);
        }
      });
    }).catch(() => setExtra((x) => ({ ...x, mediaErr: true })));
    return () => { annullato = true; stop(); pcRef.current?.close(); st?.getTracks().forEach((x) => x.stop()); };
  }, [token, active, localRef, remoteRef, setExtra]);
  const giraCamera = async () => {
    const next = facing === "user" ? "environment" : "user";
    const m = await navigator.mediaDevices.getUserMedia({ video: { ...VIDEO_360, facingMode: next } });
    const tr = m.getVideoTracks()[0];
    const sender = pcRef.current?.getSenders().find((x) => x.track?.kind === "video");
    if (sender) await sender.replaceTrack(tr);
    stream?.getVideoTracks().forEach((x) => { stream.removeTrack(x); x.stop(); });
    stream?.addTrack(tr); if (localRef.current) localRef.current.srcObject = stream;
    setFacing(next);
  };
  return { giraCamera };
}

export default function VideoCliente() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [active, setActive] = useState(false);
  const [extra, setExtra] = useState({});
  const localRef = useRef(null), remoteRef = useRef(null);
  useEffect(() => { axios.get(`${API}/${token}`).then(({ data }) => setInfo(data)).catch((e) => setErr(msg(e, "Link non valido"))); }, [token]);
  const { giraCamera } = useClientCall(token, active, localRef, remoteRef, setExtra);
  useEffect(() => { if (extra.fine) setActive(false); }, [extra.fine]);
  const entra = async () => {
    try { if (!info.consenso_at) await axios.post(`${API}/${token}/consenso`); setActive(true); } catch (e) { setErr(msg(e, "Errore")); }
  };
  const live = { ...info, ...extra };
  if (err) return <Shell><div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3" data-testid="vc-err">{err}</div></Shell>;
  if (!info) return <Shell><p className="text-slate-400">Caricamento…</p></Shell>;
  if (extra.fine) return <Shell><div className="rounded-lg bg-emerald-50 text-emerald-800 p-4" data-testid="vc-fine">La videochiamata è terminata. Grazie, {info.cliente}.</div></Shell>;
  if (!active) return (
    <Shell>
      <p className="text-sm text-slate-700 mb-3">Gentile {info.cliente}, l'intermediario la attende in videochiamata.</p>
      <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm p-3 mb-3" data-testid="vc-avviso">La videochiamata <b>sarà registrata</b> (audio e video), come previsto per la vendita interamente a distanza e descritto nell'informativa che ha accettato. Durante la chiamata le verrà chiesto di mostrare il volto e un documento d'identità. Se non desidera essere registrato/a può concludere il contratto in presenza.</div>
      {!info.consenso_at && <label className="flex items-start gap-2 text-sm text-slate-700 mb-3"><input type="checkbox" className="mt-1" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="vc-consenso-check" /> Acconsento alla registrazione della videochiamata.</label>}
      <button onClick={entra} disabled={!info.consenso_at && !ok} className="w-full bg-teal-700 text-white rounded-lg py-2.5 font-medium disabled:opacity-50" data-testid="vc-entra">{info.consenso_at ? "Rientra nella videochiamata" : "Acconsento e avvia la videochiamata"}</button>
      <p className="text-[11px] text-slate-400 mt-3">Le verrà chiesto il permesso di usare fotocamera e microfono.</p>
    </Shell>
  );
  return (
    <div className="min-h-screen bg-slate-950 p-3 space-y-3" data-testid="vc-call">
      {live.registrazione && <div className="bg-red-600 text-white text-sm font-semibold text-center rounded-lg py-1.5" data-testid="vc-rec">● Registrazione in corso</div>}
      {extra.mediaErr && <div className="bg-red-50 text-red-700 text-sm rounded-lg p-3" data-testid="vc-media-err">Non è stato possibile usare fotocamera e microfono: verifichi i permessi del browser e ricarichi la pagina.</div>}
      <div className="relative bg-black rounded-xl overflow-hidden aspect-[3/4] sm:aspect-video max-w-3xl mx-auto">
        <video ref={remoteRef} autoPlay playsInline className="w-full h-full object-contain" data-testid="vc-remote" />
        <video ref={localRef} autoPlay playsInline muted className="absolute bottom-3 right-3 w-28 rounded-lg border border-white/30" data-testid="vc-local" />
      </div>
      <div className="max-w-3xl mx-auto space-y-3">
        <button onClick={giraCamera} className="w-full bg-white/10 text-white rounded-lg py-2 text-sm" data-testid="vc-gira">Cambia fotocamera (per inquadrare il documento)</button>
        {live.dati_identita && <ConfermaDati token={token} info={live} />}
      </div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-lg mx-auto bg-white rounded-2xl border border-slate-200 p-6" data-testid="vc-shell">
        <div className="text-xs font-semibold tracking-wide text-teal-700 mb-1">ASSIETICA · VIDEOCHIAMATA</div>
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Videochiamata con il suo intermediario</h1>
        {children}
      </div>
    </div>
  );
}
